/**
 * GENERATED from source/attractors.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const ATTRACTORS = {
  "key": "attractors-metastability",
  "title": "Attractors, Multistability & Metastability",
  "category": "Concepts",
  "platform": {
    "key": "attractors-metastability",
    "sub": "Stable Enough to Trust. Flexible Enough to Adapt.",
    "duration": "8–10 min read",
    "chapter": {
      "id": "attractors-metastability",
      "title": "Attractors, Multistability & Metastability"
    }
  },
  "cert": {
    "key": "attractors-metastability-cert",
    "duration": "25 min"
  },
  "chapters": [
    {
      "id": "intro",
      "title": "Why Athletes Keep Returning to the Same Solution",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Why Athletes Keep Returning to the Same Solution"
        },
        {
          "kind": "text",
          "md": "Think about a player whose crossover always looks similar.\n\nThe width of the ball, the width of their base, and the timing of the movement seem to settle into the same pattern. Even when a narrower or wider solution might work better, they keep finding their way back to their familiar one.\n\nOr consider a shooter who seems to have one preferred release point. The distance changes. Their momentum changes. The amount they jump changes. Yet their release keeps organizing in nearly the same place.\n\nThese are useful examples of what movement scientists call **attractors**."
        },
        {
          "kind": "bigIdea",
          "md": "An attractor is a stable pattern of coordination that a movement system tends to return toward."
        },
        {
          "kind": "text",
          "md": "In simpler coaching language: **it's a solution the athlete keeps falling back into.**\n\nAn attractor is more than a single body position. It can involve the timing and relationship between several moving parts. A repeated movement feature can be a clue, but one similar-looking rep does not prove an attractor exists.\n\nAttractors are not inherently bad. We want athletes to have reliable solutions. If every shot, crossover, or finish had to be figured out from scratch, skilled performance would be almost impossible.\n\nThe question is what happens when the situation changes."
        },
        {
          "kind": "bigIdea",
          "md": "A good default helps you solve problems. A limiting default keeps appearing even when the problem calls for something different."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Picture a Landscape of Valleys"
        },
        {
          "kind": "text",
          "md": "Imagine rolling a ball across a landscape.\n\nWhen it reaches a valley, it naturally settles toward the bottom. Nudge it and, if the valley is stable enough, it rolls back.\n\nThat is the basic metaphor for an attractor.\n\n- **One very deep valley:** One extremely strong default. Reliable, but potentially difficult to leave.\n- **Almost no clear valleys:** Few dependable patterns to settle into. The athlete may access different movements but struggle to organize them consistently.\n- **Several accessible valleys:** Multiple relatively stable ways to solve the problem. Each can be reliable, while the athlete has alternatives when conditions call for them."
        },
        {
          "kind": "conceptGraphic",
          "name": "landscapes",
          "alt": "Three conceptual attractor landscapes",
          "caption": "Illustration note: This is a coaching analogy, not a measured landscape of a basketball player's body or brain. Valley depth, number, and distance are conceptual, not quantities we can diagnose from video."
        },
        {
          "kind": "text",
          "md": "The third landscape sounds appealing. But it reveals something important:\n\n**We don't just want athletes who can move anywhere. We want useful solutions they can reliably access.**\n\nAnd we don't necessarily want every valley to become shallow. An athlete can have a very stable movement solution and other stable options for different situations."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Two Useful Ideas: Multistability and Metastability"
        },
        {
          "kind": "text",
          "md": "These terms are related, but they do not mean the same thing."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Multistability: More Than One Stable Option"
        },
        {
          "kind": "text",
          "md": "A system is **multistable** when multiple stable organizations are available under the relevant conditions.\n\nThe practical shooting picture is an athlete who has more than one reliable release organization: perhaps a lower release they can access for some deeper shots, a middle release, and a higher release that another situation requires.\n\nWe are using this as an illustration of the concept, **not claiming those three releases have been scientifically measured as three attractors**."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Metastability: Temporary Coordination Without Getting Locked In"
        },
        {
          "kind": "text",
          "md": "In coordination dynamics, **metastability** describes temporary tendencies to coordinate, with the ability to move out of them rather than remain permanently locked in one stable state.\n\nSo the basic coaching picture is the ability to **organize, release, and reorganize** as conditions change.\n\n**One crucial distinction:** metastability does **not** simply mean “three shallow valleys.” In some formal models, metastable coordination occurs without stable attractor wells at all. The system temporarily lingers around preferred relationships and moves on.\n\nYou do not need to prove that a basketball player has a mathematically metastable movement system to use the coaching lesson."
        },
        {
          "kind": "bigIdea",
          "md": "Build stability within useful solutions and flexibility in moving between them."
        },
        {
          "kind": "text",
          "md": "That is BAM's practical interpretation, not a claim that more metastability is always better."
        },
        {
          "kind": "scienceNerd",
          "title": "What Stability Actually Means",
          "blocks": [
            {
              "kind": "prose",
              "md": "In coordination dynamics, an attractor is a stable state or pattern in a dynamical description of the system. **Stability is about how a system behaves when disturbed**, not simply whether a technique looks consistent.\n\nImagine two coordination patterns that look equally repeatable during easy practice. One returns quickly after a small disturbance; the other breaks down and reorganizes somewhere else. In the dynamical sense, those patterns differ in stability.\n\nResearchers examine properties such as fluctuations, return after perturbations, and transitions as task demands change. The valley visualization translates this idea: a deeper basin represents a stronger tendency to return. It is not a literal measurement of effort, muscle stiffness, or conscious resistance.\n\nThe concept has been studied most cleanly in controlled coordination tasks, especially rhythmic movements involving two limbs. Researchers have observed that an initially stable pattern can become unstable when movement frequency changes, producing a transition into another coordination pattern.\n\nThat is a useful scientific foundation for thinking about basketball, **but we have not demonstrated that a particular crossover width or release point behaves according to the same measured mathematical model**.\n\n**Source:** Kelso (2012), Multistability and metastability: understanding dynamic coordination in the brain, Philosophical Transactions of the Royal Society B. [Open review](https://pmc.ncbi.nlm.nih.gov/articles/PMC3282307/)."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "A More Exact Distinction",
          "blocks": [
            {
              "kind": "prose",
              "md": "**Multistability** means the system possesses more than one stable state. It can settle into one state, and the right changes in conditions or perturbations may cause it to transition to another. Multiple states existing does not guarantee the system switches between them easily or appropriately. **Strictly, multistability means multiple stable states are available under comparable system conditions.** A shooter using one release at long range and another at close range may simply be adapting to different task demands. We should not call that measured multistability without testing whether the alternatives coexist under the same relevant conditions.\n\n**Metastability**, in the specific coordination-dynamics framework emphasized by Kelso, combines tendencies toward coupling with tendencies for components to retain some independence. The system dwells near preferred coordination relationships and then leaves them. In the relevant formal regime, **stable phase-locked attractors are absent**, even though attracting tendencies remain.\n\nThat's why the phrase **“many shallow valleys = metastability”** is misleading.\n\nA recent scientific perspective also notes that metastability has been defined in different ways across neuroscience. For this module, we use the coordination-dynamics meaning above, while acknowledging that broader usage exists. ([Reference](https://pubmed.ncbi.nlm.nih.gov/40103058/))\n\n**What coaches need to retain:** reliable organization and freedom to reorganize are complementary properties. Neither “stronger attractors everywhere” nor “maximum flexibility everywhere” is a universal target."
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "limitation",
      "title": "When Does a Good Attractor Become a Limitation?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "When Does a Good Attractor Become a Limitation?"
        },
        {
          "kind": "text",
          "md": "The issue isn't whether the athlete has a strong default. It's **whether that default continues to work across the situations they actually face**.\n\nReturn to the shooter whose release height barely changes.\n\nThey might still make shots at different distances or while jumping different amounts. Does that mean their release itself is adapting?\n\nNot necessarily.\n\nThey might keep the release very similar while changing **something around it**, such as the timing of the jump, even releasing as they descend.\n\nThe athlete adapted at the level of the whole task. But the release organization may have stayed quite rigid.\n\nThat distinction matters:\n\n- **Task adaptability:** Can they still achieve the basketball outcome as conditions change?\n- **Movement adaptability:** Can they adjust the particular part of the movement we're examining?"
        },
        {
          "kind": "bigIdea",
          "md": "An athlete can be adaptable without every part of their movement being adaptable."
        },
        {
          "kind": "text",
          "md": "And that's not automatically a problem. Compensation is often useful. We only need to intervene if the rigid component or the workaround meaningfully limits the athlete: fewer usable options, poor robustness, excess complexity, or unwanted performance costs.\n\nDon't “fix” stability just because it looks fixed."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Compensation Is Not Automatically Cheating the System",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "In coordinated actions, different movement components can sometimes compensate for one another to preserve a shared task outcome. This idea is closely related to the study of **motor synergies** and **motor abundance**.\n\nFor a coach, this means there are at least three different possibilities when an outcome stays stable:\n\n1. The focal movement feature adapts to the changed situation.\n2. The focal feature stays stable while other movement components compensate.\n3. Several features co-adapt in different combinations.\n\nThose are different explanations for the same apparent success.\n\nThe practical diagnostic is not “Did they make the shot?” alone. It's “**What actually changed to allow the shot to work?**”\n\nThere is no reason to assume option 2 is inferior. Sometimes preserving one robust feature while adjusting others is exactly what makes skill useful. The coach must judge whether the compensation creates meaningful limitations.\n\n**Research connection:** the motor-synergy literature studies how variation across elements can maintain task-level performance. See The Effects of Practice on Coordination ([open article](https://pmc.ncbi.nlm.nih.gov/articles/PMC3897239/)). This provides a framework for the compensation principle, **not direct proof** that any one basketball shooter is using a particular attractor.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "four",
      "title": "Four Things a Coach Might Want to Do",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Four Things a Coach Might Want to Do"
        },
        {
          "kind": "text",
          "md": "Once you identify an attractor-like preference, the next step is not automatically to get rid of it."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "1. Strengthen"
        },
        {
          "kind": "text",
          "md": "The athlete has a useful solution, but they don't access it reliably enough yet. The objective is to make that organization more dependable."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "2. Add"
        },
        {
          "kind": "text",
          "md": "The athlete's current solution works, but they need another option for a different situation. We want them to develop and stabilize another useful organization."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "3. Shift"
        },
        {
          "kind": "text",
          "md": "The athlete keeps returning to a movement organization that is limiting them. We want a more functional organization to become their natural preference."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "4. Reconnect"
        },
        {
          "kind": "text",
          "md": "The athlete can execute multiple solutions when prompted, but they do not access the right one when the game asks for it. We want to reconnect those solutions to **the information that makes each one useful**.\n\nThese are **BAM coaching goals**, not four formally established categories of attractor dynamics.\n\nThey also aren't mutually exclusive. You might add an option, then need to strengthen it, then reconnect it to a game situation."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Adding a Solution vs. Changing a Preferred Solution"
        },
        {
          "kind": "text",
          "md": "There is a big difference between these two coaching problems."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Add a solution"
        },
        {
          "kind": "text",
          "md": "A shooter has a release that works well most of the time, but struggles when the situation asks for a different release height.\n\nYou might not want to replace the original shot. You want to make another release organization accessible and reliable."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Shift a solution"
        },
        {
          "kind": "text",
          "md": "A shooter releases so far to the left of their face that the organization is consistently limiting.\n\nYou might want to develop a more functional release and help it become the new default.\n\nThe purpose isn't necessarily to make the old version physically impossible. It's to reduce how often it dominates when a better solution is available."
        },
        {
          "kind": "bigIdea",
          "md": "Sometimes development means adding another valley. Sometimes it means changing where the athlete naturally settles."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "A Real Research Finding: Two Routes to Learning",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Kostrubiec, Zanone, Fuchs, and Kelso (2012) examined how people learn new coordination patterns using controlled **bimanual movement tasks** and a dynamical model.\n\nBefore training, learners differed in the coordination patterns they could spontaneously stabilize. That mattered for how they learned what came next.\n\nThe researchers described two routes:\n\n**Bifurcation route:** a new stable coordination pattern emerges in the repertoire. The landscape gains another stable option.\n\n**Shift route:** an existing preferred coordination pattern changes location toward the newly learned target.\n\nIn their account, the learner's starting repertoire influenced which route occurred. Their modeling also indicated that the bifurcation route could produce greater stabilization of the newly learned behavior in the studied task.",
          "depth": "cert"
        },
        {
          "kind": "conceptGraphic",
          "name": "addShift",
          "alt": "Adding a new movement attractor versus shifting a preferred pattern",
          "caption": "Research-inspired conceptual diagram, not a reproduction of the experiment or a measurement of basketball technique.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "This is a compelling connection to BAM's **add versus shift** distinction. But keep the boundary clear:\n\n- These are experimental findings and modeling in rhythmic coordination, **not basketball shooting**.\n- They do not mean a coach can reliably select a bifurcation or shift simply by choosing a certain drill.\n- They do support the broader idea that learning can **change the organization of an existing repertoire**, not merely make one movement more accurate.\n\n**Source:** Kostrubiec et al. (2012), Beyond the blank slate: routes to learning new coordination patterns depend on the intrinsic dynamics of the learner, Frontiers in Human Neuroscience. [Full article](https://www.frontiersin.org/journals/human-neuroscience/articles/10.3389/fnhum.2012.00222/full).",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "leave",
      "title": "How Do We Help an Athlete Leave a Limiting Attractor?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "How Do We Help an Athlete Leave a Limiting Attractor?"
        },
        {
          "kind": "text",
          "md": "We have several tools. The goal isn't just to make them do something different once. It's to help that alternative become **accessible, useful, and eventually reliable**."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Differential Learning"
        },
        {
          "kind": "text",
          "md": "Deliberately exaggerate movement differences around the athlete's default. That can expose the athlete to organizations they rarely access and may help loosen a rigid preference."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Constraints"
        },
        {
          "kind": "text",
          "md": "Change the task so their favorite solution isn't available or isn't always rewarded. Create reasons to find other solutions."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Build action capacity"
        },
        {
          "kind": "text",
          "md": "If the alternative movement is genuinely outside their current ability, it may need to be developed before the athlete can use it in game conditions."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Strengthen the new solution"
        },
        {
          "kind": "text",
          "md": "Experience alone isn't necessarily enough. Give the athlete further opportunities to organize the alternative and return to it consistently."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Reintroduce real information"
        },
        {
          "kind": "text",
          "md": "Once the athlete can execute the alternative, reduce artificial steering. Let the situation tell them when it should emerge."
        },
        {
          "kind": "bigIdea",
          "md": "Forcing a new solution in a drill is not the same as making it available in a game."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Basketball Example: Changing a Release"
        },
        {
          "kind": "text",
          "md": "Say we're trying to help a shooter access a higher release.\n\nTo encourage a higher release, adjust the task so that releasing higher becomes useful or necessary. For example, the athlete could jump to high-five a partner before shooting, or shoot over an outstretched arm while organizing a higher jump and release.\n\nTo encourage a lower release, shooting from farther back without jumping can make a different release organization more accessible or necessary.\n\nThese are **possible practice manipulations**, not guarantees about exactly how every athlete will respond. If the manipulation causes a different unwanted compensation, we'd change it.\n\nAnother example involves crossover or footwork organization: a footwork mat can temporarily narrow the allowed base, widen it, or emphasize another movement range.\n\nThe progression matters:\n\n**Explore a different solution → make it more stable → remove the artificial demand → see whether the game invites it.**"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Should We Try to Erase an Old Attractor?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Usually, a more practical goal is to **make the better organization more dominant**, not to prove the older one has been erased.\n\nMotor-learning studies provide an important warning here. A previous movement adaptation can sometimes reappear more quickly upon relearning, a phenomenon called **savings**, even after performance seems to have returned toward baseline.\n\nBut savings is not universal across every protocol, and the mechanisms depend on how learning and washout occur. Studies of sensorimotor adaptation cannot tell us whether an athlete's old shot will survive forever.\n\nWhat they do tell us is that **not seeing an old behavior is not the same as proving its memory no longer exists**.\n\nFor BAM, the practical target is observable:\n\n- Does the new solution become a reliable default when appropriate?\n- Does the older one still reappear in conditions where it limits the athlete?\n- Does the newer solution hold up when cueing decreases, the task changes, or time passes?\n\nA player who returns to the old pattern occasionally may still be making meaningful progress. The question is what that return costs them and whether the new preference is strengthening.\n\n**Sources:** [Unlearning versus savings in visuomotor adaptation (2013)](https://pmc.ncbi.nlm.nih.gov/articles/PMC3711055/); [A double dissociation between savings and long-term memory in motor learning (2023)](https://pmc.ncbi.nlm.nih.gov/articles/PMC10138789/).",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "decisions",
      "title": "What About Decision-Making Attractors?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "What About Decision-Making Attractors?"
        },
        {
          "kind": "text",
          "md": "The attractor lens is useful beyond visible movement coordination, but we need to use it carefully.\n\nA player may repeatedly choose the same action, even when the situation changes.\n\nFor example, a player who loves driving left may keep driving left after a defender cuts it off, instead of countering right.\n\nOr a player who sees ICE coverage on a ball screen may keep asking for a screen flip, even when another action could fit the situation better.\n\nIt's tempting to say, “They have a decision attractor.” As BAM coaching language, that can be helpful.\n\nBut **repeated behavior does not prove a mathematically identified attractor exists in that player's decision system**.\n\nMore importantly, **the reason the choice keeps repeating matters**.\n\nThey might:\n\n- lack the movement capacity for the alternative,\n- fail to notice the relevant affordance,\n- recognize the option but not trust it,\n- or simply favor a response that has worked very well in the past.\n\nSeveral of those can be true at once."
        },
        {
          "kind": "bigIdea",
          "md": "Is the athlete choosing the familiar solution because it's the best one, or because it's the only one they can reliably access?"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Decision Attractors: What the Science Supports, and What It Doesn't",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Attractor-based models are used in decision neuroscience, not just movement science. For example, a 2023 study of **macaque prefrontal activity** linked properties of modeled neural attractor dynamics with choice consistency and decision confidence.\n\nThat supports using attractor dynamics as a legitimate scientific framework for studying decisions.\n\nIt does **not** demonstrate that a specific basketball choice, like flipping a screen versus ICE coverage, is a measured neural attractor.\n\nFor coaches, it's usually safer to say **habitual or strongly preferred decision solution**, then use attractor language as an analogy when helpful.\n\nAnd there's a connection to Affordances: the athlete may not perceive the alternative, may lack the action capacity to use it, or may have an established preference that dominates even when other options are available. These are different diagnoses.\n\n**Source:** Attractor dynamics reflect decision confidence in macaque prefrontal cortex, Nature Neuroscience (2023). [Article](https://www.nature.com/articles/s41593-023-01445-x).",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Example: Exploring Other Decisions"
        },
        {
          "kind": "text",
          "md": "Take the player who keeps driving left.\n\nOne option is to temporarily make the counter back right necessary or more rewarding. For example, require the athlete to use that counter **three out of five times** to win a small-sided game.\n\nBut if the player lacks the movement capacity to counter right, the solution isn't simply to keep demanding it. We may need to build that capacity separately.\n\nOr consider the player who automatically flips the screen against ICE coverage.\n\nA **ball-screen bingo** exploration task can require players to try different responses across repetitions: flip and attack, snake the action, have the big pop and play off a get, or find a skip pass.\n\nThat can expand the player's experience of alternative solutions.\n\n**But bingo doesn't prove decision-making has improved.** If the rules dictate the action, the athlete may just be obeying the rules.\n\nThe next step is to remove the requirement and see whether they select the option **when the defensive information actually makes it useful**."
        }
      ]
    },
    {
      "id": "natural",
      "title": "How Do You Know a New Solution Is Becoming Natural?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "How Do You Know a New Solution Is Becoming Natural?"
        },
        {
          "kind": "text",
          "md": "Don't judge it only by whether the athlete can do the move once with a special constraint in place.\n\nLook for a progression:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "1. Access"
        },
        {
          "kind": "text",
          "md": "They can produce the alternative when asked or when a constraint makes it necessary."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "2. Consistency"
        },
        {
          "kind": "text",
          "md": "They can return to it reliably, not just stumble into it once."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "3. Reduced steering"
        },
        {
          "kind": "text",
          "md": "They need fewer cues and fewer rules to find it."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "4. Contextual use"
        },
        {
          "kind": "text",
          "md": "They access it when relevant defenders, space, momentum, and other information call for it."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "5. Generalization and retention"
        },
        {
          "kind": "text",
          "md": "They can adapt it to related situations and still access it after time has passed.\n\nFluidity can be another clue, but it's not proof. A movement may look smooth in a drill and still fail to emerge in competition."
        },
        {
          "kind": "bigIdea",
          "md": "The real test isn't whether they can show you the new solution. It's whether they can find it when the situation asks for it."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "How Strong Is the Evidence Behind This Coaching Framework?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "We should distinguish three levels of support.\n\n**Well-supported coordination-dynamics research:** people exhibit stable preferred coordination patterns, transitions can occur when conditions change, and experimentally controlled learning can change a person's repertoire.\n\n**Useful but indirect applications:** thinking about multiple release organizations, crossover widths, and compensation as an athlete's attractor landscape. These are sensible applications of the framework, not mathematically established states measured in those basketball examples.\n\n**Coaching application:** Strengthen, Add, Shift, and Reconnect give us practical ways to organize an intervention. Ball-screen bingo and other constrained exploration tasks may help expose players to more solutions, but controlled attractor experiments do not prove that these specific basketball activities create new attractors. Judge the training by whether players can access the alternatives later, choose them when appropriate, and use them in competition.\n\nWhy this matters: a concept can be a **very useful coaching lens without being a proven explanation for every individual action**.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity1",
      "title": "ACTIVITY 1 | Identify the Default, Not Just the Error",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 1 | Identify the Default, Not Just the Error",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "default",
          "title": false,
          "numbered": false,
          "intro": "Choose a player you coach who regularly returns to the same movement or decision.\n\nWrite:",
          "steps": [
            {
              "id": "repeats",
              "q": "What solution repeats?",
              "hint": "Describe timing and coordination or the decision, not just “bad habit.”",
              "rows": 3
            },
            {
              "id": "useful",
              "q": "When is it useful?",
              "hint": "Give at least one context where the default actually works.",
              "rows": 3
            },
            {
              "id": "limiting",
              "q": "When does it become limiting?",
              "hint": "What changed in the environment?",
              "rows": 3
            },
            {
              "id": "explain",
              "q": "What else could explain it?",
              "hint": "Action capacity, perception, confidence, prior success, or another constraint?",
              "rows": 3
            },
            {
              "id": "evidence",
              "q": "What evidence would change your mind?",
              "hint": "What would make you conclude this is not a limiting attractor-like preference?",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Purpose:** recognize the tendency without treating an observed pattern as a complete diagnosis.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 2 | Strengthen, Add, Shift, or Reconnect?",
          "depth": "cert"
        },
        {
          "kind": "diagnostic",
          "id": "goals",
          "intro": "Use the following BAM situations. Choose the **primary coaching goal**, then explain what you would need to know before committing to it.",
          "prompts": [
            {
              "id": "a",
              "q": "A. A shooter has one dependable release, but struggles to access a useful alternative when momentum or distance changes."
            },
            {
              "id": "b",
              "q": "B. A shooter frequently returns to a release far to one side of the face that consistently limits performance."
            },
            {
              "id": "c",
              "q": "C. A player can counter right in a constrained drill but keeps forcing their left drive when defenders cut it off during play."
            },
            {
              "id": "d",
              "q": "D. A player has a promising new movement solution that appears inconsistently, even with the same task demands."
            }
          ],
          "discussion": "**Compare your answer:** A: Add. B: Shift. C: Reconnect, after checking action capacity and perception. D: Strengthen. None of these diagnoses is certain from a short description alone.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity3",
      "title": "ACTIVITY 3 | Build the Landscape, Then Test It",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 3 | Build the Landscape, Then Test It",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "landscape",
          "title": false,
          "numbered": false,
          "intro": "Choose one actual athlete and one solution family. Do not invent a new game scenario.",
          "steps": [
            {
              "id": "current",
              "q": "Part 1: Current landscape",
              "hint": "- What is the dominant solution?\n- What alternatives can the athlete actually access today?\n- Which solutions are useful in which contexts?\n- Are you observing genuine alternatives or variations around one fixed organization?",
              "rows": 3
            },
            {
              "id": "intervention",
              "q": "Part 2: Intervention",
              "hint": "- Are you trying to strengthen, add, shift, or reconnect?\n- What constraint or Differential Learning variation would make a different organization accessible?\n- What might the athlete compensate with instead?\n- If the alternative is unavailable, what action capacity must be built?",
              "rows": 3
            },
            {
              "id": "remove",
              "q": "Part 3: Remove the support",
              "hint": "- What does the task look like when you remove the cue or rule?\n- What information in the game should invite the alternative?\n- What would you test in a later session to see if the solution remained accessible?",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Coach check:** the intervention should not end at “the player executed the move because I forced them to.”",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 4 | Distinguish Adaptability From Compensation",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "compensation",
          "title": false,
          "numbered": false,
          "intro": "Return to the BAM shooting example: release height stays consistent across distances, but jump timing changes.\n\nAnswer:",
          "steps": [
            {
              "id": "task",
              "q": "What did the athlete successfully adapt at the task level?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "rigid",
              "q": "What movement feature may have remained rigid?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "observe",
              "q": "What would you observe to determine whether this compensation is actually limiting?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "leave",
              "q": "When would you leave it alone?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "addshift",
              "q": "If change were justified, would you add another release solution or shift the preferred release, and why?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Goal:** avoid assuming all success proves movement flexibility, or all compensation is undesirable.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 5 | The Bingo Trap",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "bingo",
          "title": false,
          "numbered": false,
          "intro": "",
          "steps": [
            {
              "id": "difference",
              "q": "Using the ICE-coverage ball-screen bingo activity, explain the difference between:",
              "hint": "- producing a skip pass because the bingo card requires it, and\n- choosing a skip pass because the defense makes it the best available option.",
              "rows": 3
            },
            {
              "id": "progression",
              "q": "Design a progression from forced exploration toward contextual decision-making. State which constraint you would remove, what information you need to preserve, and what result would count as meaningful transfer.",
              "hint": "",
              "rows": 4
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "diagnostic",
      "title": "The BAM Attractor Diagnostic",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "The BAM Attractor Diagnostic"
        },
        {
          "kind": "text",
          "md": "When you see an athlete returning to the same solution, ask:\n\n**1. What do they keep returning to?**\n\n**2. Is that solution useful, limiting, or simply preferred?**\n\n**3. Why does it dominate?**\n\nAction capacity? Perception? Confidence? Past success? A stable coordination preference?\n\n**4. What do we actually need?**\n\nStrengthen it? Add an alternative? Shift the preferred organization? Reconnect available options to the game?\n\n**5. Does the alternative survive when we stop forcing it?**\n\nThose five questions will usually help more than immediately correcting the movement."
        },
        {
          "kind": "reflection",
          "md": "Think of one player with a movement or decision they return to over and over.\n\n**Is it a useful default, a limiting default, or simply the only option they currently trust?**\n\nThen ask yourself: **Do they need a better version of the same solution, another solution, or a better ability to recognize when to switch?**",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection | Stability Without Rigidity",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "reflection",
          "title": false,
          "numbered": false,
          "intro": "Choose one athlete or skill you've spent a lot of time developing.",
          "steps": [
            {
              "id": "protect",
              "q": "What movement or decision preferences are already worth protecting?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "limiting",
              "q": "Which preferred solution becomes limiting when conditions change?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "available",
              "q": "What evidence suggests an alternative is actually available to the athlete?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "hiding",
              "q": "How might their success be hiding compensation around a rigid movement component?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "goal",
              "q": "Which goal fits best: strengthen, add, shift, or reconnect?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "structure",
              "q": "How would you structure practice so that the new solution is both reliable and appropriately selected?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "test",
              "q": "What delayed, less-constrained test would convince you the change has persisted?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Finish this sentence:",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "sentence",
          "prompt": "The goal isn't to remove stability. It's to __________.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Takeaway"
        },
        {
          "kind": "text",
          "md": "Skilled athletes develop solutions they can return to. They should.\n\nAttractors are part of what makes movement dependable.\n\nBut a solution that is reliable in one context can become limiting in another, especially if the athlete cannot access a different organization when the situation demands it.\n\nSometimes the answer is to strengthen their default.\n\nSometimes it is to add another option.\n\nSometimes it is to shift where they naturally organize.\n\nAnd sometimes they already have the options. They just need to connect them to the right information."
        },
        {
          "kind": "bigIdea",
          "md": "Stable enough to trust. Flexible enough to adapt."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "References & Source Boundaries"
        },
        {
          "kind": "text",
          "md": "1. **Kelso, J. A. S. (2012).** [Multistability and metastability: understanding dynamic coordination in the brain](https://pmc.ncbi.nlm.nih.gov/articles/PMC3282307/). Philosophical Transactions of the Royal Society B, 367, 906–918. Foundational definitions and evidence across coordination/perception; not a basketball shooting study.\n2. **Kostrubiec, V., Zanone, P.-G., Fuchs, A., & Kelso, J. A. S. (2012).** [Beyond the blank slate: routes to learning new coordination patterns depend on the intrinsic dynamics of the learner](https://www.frontiersin.org/journals/human-neuroscience/articles/10.3389/fnhum.2012.00222/full). Frontiers in Human Neuroscience, 6, 222. Controlled bimanual coordination and model: adding versus shifting a stable state.\n3. **The Effects of Practice on Coordination.** [Open article](https://pmc.ncbi.nlm.nih.gov/articles/PMC3897239/). General movement-coordination/synergy research; supports component compensation as a framework, not any specific basketball diagnosis.\n4. **Attractor dynamics reflect decision confidence in macaque prefrontal cortex (2023).** [Nature Neuroscience](https://www.nature.com/articles/s41593-023-01445-x). Decision-attractor neuroscience; not evidence of identified attractors for a basketball read.\n5. **Unlearning versus savings in visuomotor adaptation (2013).** [Open article](https://pmc.ncbi.nlm.nih.gov/articles/PMC3711055/). Motor adaptation memories, washout, and savings; not a demonstration that all prior basketball movement patterns persist.\n6. **A double dissociation between savings and long-term memory in motor learning (2023).** [Open article](https://pmc.ncbi.nlm.nih.gov/articles/PMC10138789/). Further distinctions between savings and lasting memory.\n7. **Dynamical properties and mechanisms of metastability: A perspective in neuroscience (2025).** [PubMed](https://pubmed.ncbi.nlm.nih.gov/40103058/). Notes variation in formal usage of metastability across neuroscience."
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "degrees-of-freedom",
              "title": "Degrees of Freedom",
              "blurb": "Freedom Is Only Useful If You Can Organize It."
            },
            {
              "key": "differential-learning-concept",
              "title": "Differential Learning",
              "blurb": "Push the Borders. Reorganize the Skill."
            },
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            },
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "types-of-variability",
              "title": "Types of Variability",
              "blurb": "Stable enough to perform. Adaptable enough to solve."
            },
            {
              "key": "challenge-point",
              "title": "Challenge Point & Desirable Difficulties",
              "blurb": "Hard Is Not the Goal. Productive Struggle Is."
            },
            {
              "key": "types-of-constraints",
              "title": "Types of Constraints",
              "blurb": "Design the Problem. Let the Athlete Solve It."
            }
          ]
        }
      ]
    }
  ]
};
